import { QueryErrorState } from "@/api/QueryErrorState";
import { isServerAnsweredFailure } from "@/api/queryClient";
import { OfflineState } from "@/ui/states";

/**
 * A read a named surface depends on that did not answer, and the one state that
 * describes it honestly (#492).
 *
 * ONE COMPONENT BECAUSE THE DECISION IS ONE DECISION. Five surfaces in this
 * family block on a read they do not own — the catalog, a background set's head,
 * an asset's head, the copy source, the part manifest — and each of them has to
 * make the same three-way choice about the failure it is holding:
 *
 *   1. **a pause with no response ever received** is the CONNECTION, and the
 *      only case that may borrow the offline vocabulary. `isServerAnsweredFailure`
 *      is the single fact that decides it: `ApiError` exists if and only if the
 *      request completed and the service put a status on it;
 *   2. **a pause over an answered failure** is that failure, still holding. It
 *      says what the service said, and it keeps its retry — a paused failure is
 *      never terminal, because `queryClient`'s `retry` rejects a terminal status
 *      before the retryer ever sleeps;
 *   3. **a settled failure** routes through `QueryErrorState`, which is where
 *      401, 403, 404 and 501 already have their established recoveries and where
 *      the retry is WITHHELD for a status no retry can change.
 *
 * Getting any one of the three wrong has a name in this repository: (1) is #327,
 * (2) is #215, and (3) is the dead "Try again" #4 forbids. Making the choice
 * once is what keeps a surface added later — #493's pathway families reuse this
 * module — from rediscovering all three.
 *
 * WHY THE RETRY IS A PROP RATHER THAN BUILT HERE. `QueryPendingState` builds its
 * own from a query key, which is right for a surface whose subject is one query.
 * These surfaces block on a SET whose membership is data — one head read per
 * background set — so the key list is not knowable at this component's call
 * site. The caller supplies a recovery it has already built with
 * `useRetryPausedQuery`, `useRetryPausedQueryByKey` or `retryFailed`; what it
 * must never supply is a bare `refetch`, which fires nothing in case (1) and (2)
 * — precisely the cases that offer the control.
 */
export interface NamedReadFailure {
  /**
   * The failure itself: `error` once settled, `failureReason` while held.
   *
   * Typed `unknown` deliberately — a transport that never connected rejects with
   * whatever `fetch` threw, and narrowing that to `Error` here would be this
   * module claiming a shape it did not receive.
   */
  failure: unknown;
  /** Nothing is in flight; the retry is being held. */
  isPaused: boolean;
  /** A cancel-then-refetch recovery, never a bare `refetch`. */
  retry: () => void;
}

export function NamedReadFailureState({
  failure,
  isPaused,
  retry,
  subject,
  title,
  description,
  deniedDescription,
  offlineDescription,
}: NamedReadFailure & {
  /** Noun for the not-implemented heading, e.g. "Background set documents". */
  subject: string;
  /** Heading for the transient-failure case. */
  title: string;
  description: string;
  deniedDescription: string;
  /** What this particular surface loses while the connection is gone. */
  offlineDescription?: string;
}) {
  if (isPaused && !isServerAnsweredFailure(failure)) {
    return (
      <OfflineState
        onRetry={retry}
        {...(offlineDescription !== undefined
          ? { description: offlineDescription }
          : {})}
      />
    );
  }

  return (
    <QueryErrorState
      error={failure}
      subject={subject}
      title={title}
      description={description}
      deniedDescription={deniedDescription}
      onRetry={retry}
    />
  );
}
